import { apiRequestNew } from '@/helpers/request'
import type { T_CertificatesListRes } from '@/types/responses'

const PENDING_ARCHIVE_LS_PREFIX = 'ERA_PENDING_ARCHIVE_CERTIFICATES'
export const PENDING_ARCHIVE_UPDATED_EVENT = 'era:pending-archive-updated'
const MAX_PENDING_ARCHIVE_AGE_MS = 72 * 60 * 60 * 1000

export type T_PendingArchiveItem = {
  cert: T_CertificatesListRes[number]
  movedAt: number
}

export const getPendingArchiveQueueKey = (userId?: number | null) => (
  `${PENDING_ARCHIVE_LS_PREFIX}_${userId || 0}`
)

const normalizePendingArchiveQueue = (raw: unknown): T_PendingArchiveItem[] => {
  if (!Array.isArray(raw)) return []
  const now = Date.now()

  const normalized = raw
    .map((item: any) => {
      const cert = item?.cert
      const movedAt = Number(item?.movedAt)
      if (!cert || typeof cert !== 'object') return null
      const certId = Number(cert.id)
      if (!(certId > 0)) return null
      const normalizedMovedAt = Number.isFinite(movedAt) && movedAt > 0 ? movedAt : Date.now()
      if ((now - normalizedMovedAt) > MAX_PENDING_ARCHIVE_AGE_MS) return null
      return {
        cert: { ...cert, id: certId },
        movedAt: normalizedMovedAt,
      } as T_PendingArchiveItem
    })
    .filter(Boolean) as T_PendingArchiveItem[]

  const dedupById = new Map<number, T_PendingArchiveItem>()
  for (const item of normalized.sort((a, b) => b.movedAt - a.movedAt)) {
    if (!dedupById.has(item.cert.id)) dedupById.set(item.cert.id, item)
  }
  return [ ...dedupById.values() ]
}

export const readPendingArchiveQueue = (userId?: number | null): T_PendingArchiveItem[] => {
  try {
    const raw = localStorage.getItem(getPendingArchiveQueueKey(userId))
    if (!raw) return []
    return normalizePendingArchiveQueue(JSON.parse(raw))
  } catch {
    return []
  }
}

export const writePendingArchiveQueue = (
  userId: number | null | undefined,
  items: T_PendingArchiveItem[],
) => {
  try {
    const key = getPendingArchiveQueueKey(userId)
    const normalized = normalizePendingArchiveQueue(items)
    const nextRaw = JSON.stringify(normalized)
    const prevRaw = localStorage.getItem(key)
    if (prevRaw === nextRaw) return

    localStorage.setItem(key, nextRaw)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(PENDING_ARCHIVE_UPDATED_EVENT))
    }
  } catch {
    // ignore localStorage failures
  }
}

export const upsertPendingArchiveQueueItem = (
  userId: number | null | undefined,
  nextItem: T_PendingArchiveItem,
) => {
  const prev = readPendingArchiveQueue(userId)
  const next = [
    nextItem,
    ...prev.filter((p) => p.cert.id !== nextItem.cert.id),
  ]
  writePendingArchiveQueue(userId, next)
}

export const removePendingArchiveQueueItem = (
  userId: number | null | undefined,
  certificateId: number,
) => {
  const prev = readPendingArchiveQueue(userId)
  const next = prev.filter((p) => p.cert.id !== certificateId)
  writePendingArchiveQueue(userId, next)
}

export const flushPendingArchiveQueueToServer = async (
  userId: number | null | undefined,
) => {
  const pending = readPendingArchiveQueue(userId)
    .sort((a, b) => a.movedAt - b.movedAt)

  let atLeastOneSucceeded = false
  let activeIds: Set<number> | null = null
  let isActivePrecheckAvailable = false

  for (const item of pending) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) break
    if (!isActivePrecheckAvailable) {
      try {
        const active = await apiRequestNew<T_CertificatesListRes>('GET', '/era/gift/certificate?offset=0&limit=9999')
        activeIds = new Set((active || []).map((c) => Number(c.id)))
        isActivePrecheckAvailable = true
      } catch {
        // If precheck fails, fallback to direct DELETE attempt.
        activeIds = null
        isActivePrecheckAvailable = true
      }
    }
    if (activeIds && !activeIds.has(Number(item.cert.id))) {
      // Already not in active slice: do not spam DELETE that will likely return 404.
      continue
    }
    try {
      await apiRequestNew('DELETE', `/era/gift/certificate/${item.cert.id}`)
      if (activeIds) {
        activeIds.delete(Number(item.cert.id))
      }
      atLeastOneSucceeded = true
    } catch (err: any) {
      // Idempotent behavior: if backend says "not found", treat as already archived.
      if (Number(err?.response?.status) === 404) {
        atLeastOneSucceeded = true
        continue
      }
      // keep item in queue; next online cycle will retry
    }
  }

  return atLeastOneSucceeded
}
